/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as React from 'react';
import { currentTemplateVar, modalRefVar } from '../../../../../apollo/reactiveVariables/TemplateManagementVariables';
import { PanelRegion, PanelType, ScenarioName, StatusName, StepName, SubWorkLoad } from '../../../../../logger';
import { ApprovalTemplate, ListApprovalDefaultTemplatesClient } from '../../../../../service/gen/templates';
import { getAppHost } from '../../../../../utilities/TeamsUtils';
import { useIsAdmin } from '../../../../../utilities/TemplateManagementUtils';
import { Template } from '../../../../../utilities/TemplateUtils';
import { PreSetupTemplateStepProps, PreSetupTemplateSteps } from '../PreSetupTemplateSteps';
import { TemplateStoreComponent, TemplateStoreComponentProps } from './TemplateStore.component';

export const TemplateStoreContainer: React.FC<PreSetupTemplateStepProps> = (
    props: PreSetupTemplateStepProps
): JSX.Element => {
    const host = getAppHost();
    const logger = host.getLogger();
    const { navigateTo } = props;
    const [isLoading, setIsLoading] = React.useState(true);
    const [templates, setTemplates] = React.useState<Template[]>([]);
    const [showError, setShowError] = React.useState(false);
    const isTemplatePreviewEnabled = getAppHost().getEcsConfig().MicrosoftTeamApprovals.TemplatePreviewEnabled;
    const isAdmin = useIsAdmin();

    React.useEffect((): void => {
        modalRefVar()?.current?.focus();
    }, []);

    React.useEffect((): void => {
        const startTime = performance.now();
        new ListApprovalDefaultTemplatesClient()
            .listApprovalDefaultTemplates(host.getLocaleName())
            .then((result: ApprovalTemplate[]): void => {
                const templatesResult = result.map((template: ApprovalTemplate): Template => {
                    return {
                        id: template.Id,
                        title: template.Title,
                        description: template.Description,
                        iconBackgroundColor: template.AccentColor,
                        category: template.Category,
                        iconName: template.IconName,
                        formTemplate: template.FormTemplate,
                        tag: template.Tag,
                    };
                });
                setTemplates(templatesResult);
                logger.markScenario(
                    ScenarioName.CreateTemplateInstance,
                    StepName.LoadTemplateStore,
                    StatusName.Success,
                    {
                        stepElapsed: performance.now() - startTime,
                    }
                );
            })
            .catch((error: any): any => {
                logger.logTraceHttpException('listApprovalDefaultTemplates', error?.message, error?.status);
                logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.LoadTemplateStore, StatusName.Fail, {
                    stepElapsed: performance.now() - startTime,
                });

                setShowError(true);
            })
            .finally((): void => {
                setIsLoading(false);
            });
    }, [host]);

    const componentProps: TemplateStoreComponentProps = {
        templates,
        isLoading,
        showError,
        onTemplateClick: async (template: Template): Promise<void> => {
            logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.SelectTemplateFromStore, StatusName.NA);
            logger.logUserBiAction(
                SubWorkLoad.AppInteraction,
                ScenarioName.CreateTemplateInstance,
                StepName.SelectTemplateFromStore,
                'click',
                'nav',
                'template',
                'templateCard',
                PanelRegion.Modal,
                PanelType.PreSetupTemplateInstance
            );

            currentTemplateVar(template);

            if (isTemplatePreviewEnabled) {
                navigateTo(PreSetupTemplateSteps.PreviewTemplate);
            } else {
                if (isAdmin) {
                    navigateTo(PreSetupTemplateSteps.SelectScope);
                } else {
                    navigateTo(PreSetupTemplateSteps.SelectTeam);
                }
            }
        },
        onAddFromScratchClick: async (): Promise<void> => {
            logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.SelectBuildFromScratch, StatusName.NA);
            logger.logUserBiAction(
                SubWorkLoad.AppInteraction,
                ScenarioName.CreateTemplateInstance,
                StepName.SelectBuildFromScratch,
                'click',
                'nav',
                'buildFromScratch',
                'buildFromScratchButton',
                PanelRegion.Modal,
                PanelType.PreSetupTemplateInstance
            );

            currentTemplateVar(null);
            if (isAdmin) {
                navigateTo(PreSetupTemplateSteps.SelectScope);
            } else {
                navigateTo(PreSetupTemplateSteps.SelectTeam);
            }
        },
    };

    return <TemplateStoreComponent {...componentProps} />;
};
